// Pure, DOM-free display helpers for PatientTierPanel.vue. Kept out of the
// .vue file specifically so tests/unit/tierDisplay.spec.ts can exercise them
// without a component-mount harness — this repo's yarn test:unit runs under
// plain Node (see playwright.unit.config.ts), and PatientTierPanel.vue
// itself imports via the "@/" webpack alias, which does not resolve there.
// Mirrors tierValidation.js's existing split for exactly the same reason.

export const TIER_EVENT_TYPE_LABELS = {
  UPGRADE: "เลื่อนระดับ",
  DOWNGRADE: "ลดระดับ",
  GRACE_START: "เข้าสู่ช่วงต่อสิทธิ์",
  GRACE_EXPIRE: "สิ้นสุดช่วงต่อสิทธิ์",
  MANUAL_OVERRIDE: "ล็อกระดับโดยพนักงาน",
};

export function tierEventTypeLabel(type) {
  return TIER_EVENT_TYPE_LABELS[type] || type;
}

// Mirrors getPatientTierPublic.js's own algorithm exactly (the one place the
// backend proves the intended rule for "amount to next tier" — getPatientTier
// itself returns no such field): the next tier is the ACTIVE tier with the
// LOWEST thresholdAmount that is still above qualifiedAmount, ranked by
// threshold rather than by `rank`. In the normal case (tiers configured with
// ranks ascending alongside thresholds) those agree, but thresholdAmount is
// what the backend actually sorts by, so this does too rather than assuming
// rank order. Returns null when there is no higher active tier (patient is
// already at the top of what's currently offered).
export function computeNextTierInfo(qualifiedAmount, tiers) {
  const amount = Number(qualifiedAmount) || 0;
  const candidates = (tiers || [])
    .filter(t => t && t.isActive && Number(t.thresholdAmount) > amount)
    .sort((a, b) => Number(a.thresholdAmount) - Number(b.thresholdAmount));
  const next = candidates[0];
  if (!next) return null;
  return { tier: next, amountToNext: Number(next.thresholdAmount) - amount };
}

// tierId null/undefined -> "—" (no tier, e.g. an event's fromTierId when the
// patient had none yet). Otherwise resolves against the patient's current
// tier first (cheap, always fresh) then falls back to the full tier list
// (which may include tiers the patient is no longer in — e.g. an event from
// before a downgrade), and finally to "#<id>" for a tier that has since been
// hard-deleted and so is absent from both.
export function tierNameFor(tierId, currentTier, allTiers) {
  if (tierId == null) return "—";
  if (currentTier && currentTier.id === tierId) return currentTier.name;
  const found = (allTiers || []).find(t => t.id === tierId);
  return found ? found.name : `#${tierId}`;
}
